class BannerGridExtendGallery {
  constructor(section) {
    this.section = section;
    this.cards = Array.from(section.querySelectorAll('.banner-grid-extend__card--gallery[data-gallery-image]'))
      .filter((card) => card.dataset.galleryImage && card.dataset.galleryImage.trim() !== '');

    this.modal = section.querySelector('[data-gallery-modal]');
    this.figure = this.modal ? this.modal.querySelector('.banner-grid-extend__gallery-figure') : null;
    this.viewerImage = this.modal ? this.modal.querySelector('[data-gallery-image-viewer]') : null;
    this.counter = this.modal ? this.modal.querySelector('[data-gallery-count]') : null;
    this.prevButton = this.modal ? this.modal.querySelector('[data-gallery-prev]') : null;
    this.nextButton = this.modal ? this.modal.querySelector('[data-gallery-next]') : null;
    this.closeButton = this.modal ? this.modal.querySelector('[data-gallery-close]') : null;

    this.galleryItems = this.cards.map((card) => ({
      src: card.dataset.galleryImage,
      alt: card.dataset.galleryAlt || '',
      width: Number(card.dataset.galleryWidth) || 0,
      height: Number(card.dataset.galleryHeight) || 0
    }));

    this.currentIndex = 0;
    this.previousBodyOverflow = '';
    this.preloadedImages = new Map();
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.closeTimer = null;

    if (!this.modal || !this.viewerImage || this.galleryItems.length === 0) return;

    this.preloadGalleryImages();
    this.bindCardInteractions();
    this.bindModalControls();
  }

  bindCardInteractions() {
    this.cards.forEach((card, index) => {
      const media = card.querySelector('.banner-grid-extend__media');
      if (!media) return;

      media.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open(index);
      });
    });
  }

  bindModalControls() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.showByOffset(-1));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.showByOffset(1));
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) {
        this.close();
      }
    });

    this.modal.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
    this.modal.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });

    this.handleKeyDown = (event) => {
      if (this.modal.hasAttribute('hidden')) return;

      if (event.key === 'Escape') {
        this.close();
      }

      if (event.key === 'ArrowLeft') {
        this.showByOffset(-1);
      }

      if (event.key === 'ArrowRight') {
        this.showByOffset(1);
      }
    };

    document.addEventListener('keydown', this.handleKeyDown);
  }

  open(index) {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    this.currentIndex = index;
    this.renderCurrentImage();
    this.preloadNearbyImages();

    // Keep modal anchored to the viewport regardless of section scroll position.
    if (this.modal.parentNode !== document.body) {
      document.body.appendChild(this.modal);
    }

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    this.modal.removeAttribute('hidden');
    this.modal.classList.remove('is-closing');

    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
    });

    this.modal.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.modal.classList.remove('is-open');
    this.modal.classList.add('is-closing');

    this.closeTimer = window.setTimeout(() => {
      this.modal.setAttribute('hidden', 'hidden');
      this.modal.classList.remove('is-closing');
      this.closeTimer = null;
    }, 220);

    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = this.previousBodyOverflow;
  }

  showByOffset(offset) {
    const total = this.galleryItems.length;
    this.currentIndex = (this.currentIndex + offset + total) % total;
    this.renderCurrentImage();
    this.preloadNearbyImages();
  }

  renderCurrentImage() {
    const item = this.galleryItems[this.currentIndex];

    if (this.viewerImage.src !== item.src) {
      this.viewerImage.src = item.src;
    }

    this.viewerImage.alt = item.alt;

    if (this.figure && item.width > 0 && item.height > 0) {
      this.figure.style.setProperty('--gallery-image-ratio', `${item.width} / ${item.height}`);
    }

    if (this.counter) {
      this.counter.textContent = `${this.currentIndex + 1} / ${this.galleryItems.length}`;
    }
  }

  onTouchStart(event) {
    if (this.modal.hasAttribute('hidden') || !event.changedTouches || event.changedTouches.length === 0) return;

    const touch = event.changedTouches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  onTouchEnd(event) {
    if (this.modal.hasAttribute('hidden') || !event.changedTouches || event.changedTouches.length === 0) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Natural close gesture on mobile: swipe down to dismiss the viewer.
    if (absY > absX && deltaY > 80) {
      this.close();
      return;
    }

    if (absX < 40 || absX < absY) return;

    if (deltaX < 0) {
      this.showByOffset(1);
      return;
    }

    this.showByOffset(-1);
  }

  preloadGalleryImages() {
    const preloadWork = () => {
      this.galleryItems.forEach((item) => this.preloadImage(item.src));
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preloadWork, { timeout: 800 });
      return;
    }

    window.setTimeout(preloadWork, 100);
  }

  preloadNearbyImages() {
    const total = this.galleryItems.length;
    if (total < 2) return;

    const prevIndex = (this.currentIndex - 1 + total) % total;
    const nextIndex = (this.currentIndex + 1) % total;

    this.preloadImage(this.galleryItems[prevIndex].src);
    this.preloadImage(this.galleryItems[nextIndex].src);
  }

  preloadImage(src) {
    if (!src || this.preloadedImages.has(src)) return;

    const image = new Image();
    image.decoding = 'async';
    image.src = src;
    this.preloadedImages.set(src, image);
  }
}

function initBannerGridExtendGallery(root = document) {
  const sections = root.querySelectorAll('.banner-grid-extend-section');
  sections.forEach((section) => {
    if (section.dataset.galleryInit === 'true') return;
    section.dataset.galleryInit = 'true';
    new BannerGridExtendGallery(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBannerGridExtendGallery());
} else {
  initBannerGridExtendGallery();
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initBannerGridExtendGallery(event.target);
  });
}
